<!-- Streaming-aware markdown renderer for assistant output. Wraps
     vue-stream-markdown with our config + theme binding so every chat surface
     (full page + dock) renders identically. Handles incomplete tokens while
     streaming, code highlighting (Shiki), Mermaid and KaTeX ($…$). User-typed
     messages stay plain — this is assistant-only. -->
<template>
  <Markdown
    class="ly-md"
    :content="text"
    mode="streaming"
    :is-dark="isDark"
    :enable-animate="false"
    :shiki-options="shikiOptions"
    :mermaid-options="mermaidOptions"
    :mdast-options="mdastOptions"
  />
</template>

<script setup lang="ts">
import { computed } from 'vue';
import {
  Markdown,
  type ShikiOptions,
  type MermaidOptions,
  type MdastOptions,
} from 'vue-stream-markdown';
import { useTheme } from '../../composables/useTheme';

defineProps<{ text: string }>();

const { theme } = useTheme();
const isDark = computed(() => theme.value === 'dark');

// Code blocks: a light/dark Shiki pair, switched by `isDark`.
const shikiOptions: ShikiOptions = { theme: ['github-light', 'github-dark'] };
// Mermaid: built-in light/dark themes, switched by `isDark`.
const mermaidOptions: MermaidOptions = { theme: ['default', 'dark'] };
// Allow single-dollar inline math: $E = mc^2$.
const mdastOptions: MdastOptions = { singleDollarTextMath: true };

// NOTE: raw-HTML rendering (@stream-markdown/html node renderer) is intentionally
// NOT wired. Its renderer emits a fragment root, which makes vue-stream-markdown
// (beta) spam Vue warnings (non-element Transition root + extraneous attrs).
// Assistants rarely emit raw HTML anyway — markdown/tables/code/mermaid/katex
// cover the cases. Revisit if real HTML output is needed and the lib is fixed.
</script>
